import { roundWeatherValue } from 'helpers/weatherDisplayHelpers';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import { FlexTableProps } from '../../components/FlexTable/types';
import { fieldBodyGenerator } from '../tableHelpers';
import { formatValueForWorkOrder, EMPTY_TEXT } from '../formatHelpers';
import { Locale } from '../../locales';

const WEATHER_COLUMN_WIDTHS = [
  '9.15%',
  '24.07%',
  '14.81%',
  '19%',
  '12.59%',
  '20.37%'
] as const;

const formatWeatherValue = (
  value: string | undefined,
  isWorkOrder: boolean
): string | undefined => {
  if (!isWorkOrder) {
    return value ?? EMPTY_TEXT;
  }
  return value ? formatValueForWorkOrder(value) : '';
};

export const mapWeatherToTable = (
  { start: startWeather = {}, end: endWeather = {} } = {},
  operationType: OperationType = OperationType.APPLICATION,
  weatherSlots: string[] = ['Start', 'End']
) => {
  const isWorkOrder = operationType === OperationType.WORK_ORDER;

  return [startWeather, endWeather].map((weather: any, idx: number) => {
    const temperatureValue =
      weather.temperature !== undefined && weather.temperature !== null
        ? `${roundWeatherValue(weather.temperature)}°${weather.temperatureUnit}`
        : undefined;
    const formattedTemperature = formatWeatherValue(
      temperatureValue,
      isWorkOrder
    );

    const humidityValue =
      weather.humidity !== undefined && weather.humidity !== null
        ? `${roundWeatherValue(weather.humidity)}%`
        : undefined;
    const formattedHumidity = formatWeatherValue(humidityValue, isWorkOrder);

    const windValue = weather.windDirection
      ? `${weather.windDirection} at ${roundWeatherValue(weather.windSpeed)} ${
          weather.windSpeedUnit
        }`
      : undefined;
    const formattedWind = formatWeatherValue(windValue, isWorkOrder);

    return [
      fieldBodyGenerator(weatherSlots[idx]),
      fieldBodyGenerator(
        isWorkOrder
          ? formatValueForWorkOrder(weather.skyCondition || '---')
          : weather.skyCondition,
        { paddingHorizontal: 4 }
      ),
      fieldBodyGenerator(formattedTemperature, {
        textAlign: 'center',
        paddingRight: 4
      }),
      fieldBodyGenerator(formattedHumidity, {
        textAlign: 'center',
        paddingRight: 4
      }),
      fieldBodyGenerator(formattedWind, { paddingHorizontal: 4 }),
      fieldBodyGenerator(
        isWorkOrder
          ? formatValueForWorkOrder(weather.soilMoisture || '---')
          : weather.soilMoisture,
        { paddingHorizontal: 4 }
      )
    ].map((item, cellIdx) => ({
      ...item,
      styles: {
        ...item.styles,
        width: WEATHER_COLUMN_WIDTHS[cellIdx]
      }
    }));
  });
};

const getWeatherHeaderRow = (
  locale: Locale
): FlexTableProps['tableRows'][0] => {
  const commonStyles = {
    paddingHorizontal: 8,
    fontSize: 10,
    letterSpacing: '-0.075px'
  };

  return [
    // Non-breaking space to render column without content, otherwise it's not rendered in the PDF
    {
      label: '\u00A0',
      styles: {
        width: WEATHER_COLUMN_WIDTHS[0],
        ...commonStyles
      }
    },
    {
      label: locale.weather.sky_condition,
      styles: {
        width: WEATHER_COLUMN_WIDTHS[1],
        ...commonStyles
      },
      shouldHyphenate: true
    },
    {
      label: locale.weather.temperature,
      styles: {
        width: WEATHER_COLUMN_WIDTHS[2],
        ...commonStyles
      }
    },
    {
      label: locale.weather.relative_humidity,
      styles: {
        width: WEATHER_COLUMN_WIDTHS[3],
        ...commonStyles
      }
    },
    {
      label: locale.weather.wind,
      styles: {
        width: WEATHER_COLUMN_WIDTHS[4],
        ...commonStyles
      }
    },
    {
      label: locale.weather.soil_moisture,
      styles: {
        width: WEATHER_COLUMN_WIDTHS[5],
        ...commonStyles
      }
    }
  ];
};

// eslint-disable-next-line import/prefer-default-export
export const getWeatherTableRows = (
  weather: { start?: any; end?: any } | undefined,
  locale: Locale,
  operationType: OperationType = OperationType.APPLICATION
): FlexTableProps['tableRows'] => {
  const weatherSlots = [locale.weather.start, locale.weather.end];
  const headerRow = getWeatherHeaderRow(locale);
  const bodyRows = mapWeatherToTable(weather, operationType, weatherSlots);
  return [headerRow, ...bodyRows];
};
